import {
  Component,
  OnDestroy,
  Input,
  forwardRef,
  ChangeDetectorRef,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, finalize, takeUntil } from 'rxjs/operators';

import { SnackBarConfig } from '../../configs/snackbar.config';
import { IChannel } from '../../interfaces/mtchannel.interface';
import { IPagination } from '../../interfaces/paginator.interface';
import { ChannelApiService } from '../../services/api/channel/channel-api.service';
import { trackById } from '../../util/track-by.util';

@Component({
  selector: 'lum-channel-control',
  templateUrl: './channel-control.component.html',
  styleUrls: ['./channel-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChannelControlComponent),
      multi: true,
    },
  ],
})
export class ChannelControlComponent
  implements OnDestroy, OnInit, ControlValueAccessor
{
  @Input()
  isPushOrderToOms :boolean | null = null;

  @Input()
  isPosChannel :boolean | null = false;

  @Input()
  inputClass :string = 'lum-input';

  @Input()
  bindKey :keyof IChannel | null = null; // return specific key of the IChannel object on select, if not set return the whole object

  selectedItem :IChannel | null = null;
  pagination :IPagination | null = null;
  records :IChannel[] = [];
  loadingRecords :boolean = false;

  private searchInput$ = new Subject<string>();

  trackById = trackById;

  private onDestroy$ = new Subject();

  constructor(
    private channelApiService :ChannelApiService,
    private snackbar :MatSnackBar,
    private cd :ChangeDetectorRef,
  ) {}

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  ngOnInit() :void {
    this.searchInput$
      .pipe(debounceTime(1000), takeUntil(this.onDestroy$))
      .subscribe((searchStr) => {
        this.pagination = null;
        this.records = [];
        this.getRecords(searchStr);
      });
  }

  onChange = (value :IChannel) => {};
  onTouched = () => {};

  // Called when value is written from form control
  writeValue(value :IChannel) :void {
    if (value !== null && value !== undefined) {
      if (typeof value === 'number') {
        this.getRecordById(value);
      } else if (typeof value === 'object') {
        this.selectedItem = value;
      }
    }
  }

  // Registers a function to propagate changes to the form
  registerOnChange(fn :any) :void {
    this.onChange = fn;
  }

  // Registers a function to call when the control is touched
  registerOnTouched(fn :any) :void {
    this.onTouched = fn;
  }

  displaySelectedItem() :string {
    return this.selectedItem?.label || '';
  }

  onSelectItem(selectedItem :IChannel) :void {
    if (!selectedItem || selectedItem.id === this.selectedItem?.id) {
      return;
    }

    this.selectedItem = selectedItem;
    const returnValue = this.bindKey ? selectedItem[this.bindKey] : selectedItem;
    this.onTouched();
    this.onChange(returnValue);
    this.cd.markForCheck();
  }

  onInputChange(event :InputEvent) :void {
    const searchStr = (event.target as HTMLInputElement)?.value || '';
    this.searchInput$.next(searchStr);
  }

  getRecords(searchString ?:string) :void {
    if (
      this.loadingRecords ||
      (this.pagination &&
        this.pagination.current_page === this.pagination.current_page)
    ) {
      return;
    }

    this.loadingRecords = true;
    this.cd.detectChanges();

    const params = {
      limit: '20',
      page: this.pagination ? (this.pagination.current_page + 1).toString() : '1',
      label: searchString || '',
    };

    if (this.isPosChannel !== null) {
      params['isPosChannel'] = this.isPosChannel ? '1' : '0';
    }

    if (this.isPushOrderToOms !== null) {
      params['isPushOrderToOms'] = this.isPushOrderToOms;
    }

    this.channelApiService
      .getV2(params)
      .pipe(
        debounceTime(1000),
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.loadingRecords = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (response :any) => {
          this.pagination = response.meta;
          this.records = [...this.records, ...response.data];
        },
        error: (error :any) => {
          this.snackbar.open('Failed to load channels', 'Close', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  displayFn(channel :IChannel) :string {
    return channel?.label || '';
  }

  private getRecordById(id :number) :void {
    this.loadingRecords = true;
    this.channelApiService
      .getById(id, {})
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :IChannel) => {
          if (response) {
            this.selectedItem = response;
          }
          this.loadingRecords = false;
        },
        error: () => {
          this.loadingRecords = false;
          this.snackbar.open('Failed to load channel', 'Close', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }
}
